import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'

const { logger } = Sentry;
import './Features.scss'

interface Feature {
  id: number
  image: string
  title: string
  tagline: string
  description: string
}

const features: Feature[] = [
  {
    id: 1,
    image: '/images/analyze.png',
    title: 'AI Job Post Analyzer',
    tagline: 'Know exactly what they\'re looking for.',
    description: 'Paste any job link and instantly get insights into required skills, tone, and company culture.'
  },
  {
    id: 2,
    image: '/images/resume.png',
    title: 'Smarter Resume Tailoring',
    tagline: 'Turn your experience into their perfect fit.',
    description: 'Upload or import your resume - our AI highlights and rewrites your key achievements for each job.'
  },
  {
    id: 3,
    image: '/images/job.png',
    title: 'Application Tracker',
    tagline: 'All your job applications in one place.',
    description: 'Organize and track every application, set follow-up reminders, and never lose track of where you\'ve applied.'
  },
  {
    id: 4,
    image: '/images/plan.png',
    title: 'Interview Prep Plan',
    tagline: 'Ace every interview with confidence.',
    description: 'Get likely interview questions, AI feedback, and personalized strategies to impress.'
  }
]

function Features() {
  const { loginWithPopup, isAuthenticated } = useAuth0()
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard')
    }
  }, [isAuthenticated, navigate])

  const handleTryItOut = async () => {
    setIsLoading(true)
    try {
      const width = 700
      const height = 900
      const left = window.screen.width / 2 - width / 2
      const top = window.screen.height / 2 - height / 2
      
      const popup = window.open(
        '',
        'auth0:authorize:popup',
        `width=${width},height=${height},left=${left},top=${top},resizable,scrollbars=yes,status=1`
      )
      
      await loginWithPopup({
        authorizationParams: {
          screen_hint: 'signup'
        }
      }, { popup } as any)
    } catch (error) {
      logger.error('Login error', {
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <section 
      className="py-5 bg-light"
      style={{
        backgroundImage: "url('/images/background.jpg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="bg-white rounded-4 p-4 p-md-5 shadow-sm border border-light">
              <h2 className="text-center mb-4 fw-bold" style={{
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                fontSize: '32px',
                lineHeight: '40px',
                letterSpacing: '-0.02em'
              }}>
                Meet the <span style={{ color: '#505fe2' }}>new way</span> to get hired
              </h2>
              <p className="text-center text-muted mb-5" style={{
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                fontSize: '18px',
                lineHeight: '26px'
              }}>
                Your dream job deserves more than a generic application.
                JobsReadyAI uses advanced AI to personalize every detail - so you stand out with confidence.
              </p>
              <div className="row g-4 mb-4">
                {features.map((feature) => (
                  <div key={feature.id} className="col-md-6">
                    <div className="text-center text-md-start">
                      <div className="mb-3">
                        <img 
                          src={feature.image} 
                          alt={`${feature.title} - ${feature.tagline}`}
                          className="img-fluid"
                          style={{ maxWidth: '240px' }}
                        />
                      </div>
                      <h3 className="h5 fw-semibold mb-2 text-dark">
                        {feature.title}
                      </h3>
                      <h5 className="text-muted mb-3" style={{
                        fontSize: '16px',
                        lineHeight: '24px',
                        fontStyle: 'italic'
                      }}>
                        {feature.tagline}
                      </h5>
                      <p className="text-muted mb-0" style={{
                        fontSize: '16px',
                        lineHeight: '24px'
                      }}>
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="text-center mt-5">
                <button
                  type="button"
                  className="btn btn-primary btn-lg text-uppercase fw-bold w-100"
                  aria-label="Try JobsReadyAI features"
                  onClick={handleTryItOut}
                  disabled={isLoading}
                >
                  {isLoading ? 'Loading...' : 'try it out'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Features
